import { useMutation } from '@tanstack/react-query';
import React, { createRef, useCallback, useMemo } from 'react';
import { flushSync } from 'react-dom';
import { FieldErrors, FormProvider, useForm } from 'react-hook-form';
import styled from 'styled-components';

import { Button, Icon, Input, PasswordInput, Table, TableColumn, TableVariant, Tooltip } from '@column/column-ui-kit';

import { PageActions } from '~/app/Layout/PageFrameContext';
import { SectionMfaSms } from '~/app/pages/User/Personal/SectionMfaSms';
import { SectionMfaTotp } from '~/app/pages/User/Personal/SectionMfaTotp';
import { AddPasskeyButton, SectionPasskeys } from '~/app/pages/User/Personal/SectionPasskeys';
import { CopyInput, Notification, RenderFields, SectionHeader } from '~/components';
import { useDialog } from '~/components/Dialog/Provider';
import { PendingIcon, PlaneButton } from '~/elements';
import { FeatureFlag, useFeatureFlag } from '~/lib/flags';
import { RecaptchaAction, useReCAPTCHA } from '~/lib/recaptcha';
import { isAllowedAppAccess } from '~/lib/session/isAllowedAppAccess';
import { UserModel } from '~/models';
import { DashboardUser, PlatformRole, UserRepository } from '~/repositories';
import { getRoleLabel } from '~/repositories/RoleRepository';
import { useHelpSidebarStore } from '~/stores/HelpSidebar';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { FieldStatus, FormElementAction, FormFieldStatus, FormFieldStatusWrapper, Inner, Text } from '~/styles';
import { log } from '~/util';

interface CustomDashboardUser extends DashboardUser {
  temporaryData?: {
    authenticatorApp?: string;
  };
  newPassword?: string;
  currentPassword?: string;
  newPasswordRepeat?: string;
}

const Resend = styled(Button)`
  min-width: 108px;
`;

const Action = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const StyledEmailInput = styled(Input)`
  padding-right: 124px;
`;

const RoleHelpButton = styled(Button)`
  padding: 0;
`;

const StyledTooltip = styled(Tooltip)``;

const LeaveButton = styled(Button)`
  padding: ${({ theme }) => theme.style.buttonSmallPadding};
`;

interface PlatformRow {
  id: string;
  platformName: string;
  roleName: string;
  platformId: string;
  isOwner: boolean;
  isLiveEnabled: boolean;
}

export const PagePersonal: React.FC = () => {
  const { currentPlatform, currentUser, refetchCurrentUser, setCurrentUser, setPlatform, loadPlatform, mfaStatus } =
    useSessionStore();
  const { addSuccessNotification, addDangerNotification } = useNotificationStore();
  const { openAlert } = useDialog();
  const deviceMFAEnabled = useFeatureFlag(FeatureFlag.EnableDeviceMFA);
  const passkeyAuthEnabled = useFeatureFlag(FeatureFlag.EnablePasskeyAuth);
  const featureFlagRequireLiveVerification = useFeatureFlag(FeatureFlag.EnableLiveModeMfaRequired);
  const notificationContainer = createRef<HTMLDivElement>();
  const openHelpSidebar = useHelpSidebarStore((state) => state.openHelpSidebar);

  const executeReCAPTCHA = useReCAPTCHA();

  const formMethods = useForm<CustomDashboardUser>({
    defaultValues: {
      firstName: currentUser?.firstName,
      lastName: currentUser?.lastName,
      phoneNumber: currentUser?.phoneNumber,
      email: currentUser?.email,
    },
  });

  const { handleSubmit, setValue, getValues } = formMethods;

  if (!currentUser) {
    return null;
  }

  const handleVerifyEmail = async (resend?: boolean) => {
    await currentUser.createVerifyEmail();
    log({
      name: `User email verify${resend ? ' resent' : ''}`,
      context: currentUser,
    });

    if (resend) {
      addSuccessNotification({
        content: 'Verification email resent! Check your inbox.',
      });
    }
  };

  const handleFormError = (errors: FieldErrors<CustomDashboardUser>) => {
    Object.values(errors).forEach((error) => {
      if (error.message) {
        addDangerNotification({
          content: error.message,
        });
      }
    });
  };

  const handleSave = (data: CustomDashboardUser) => {
    executeReCAPTCHA(RecaptchaAction.UpdateUser)
      .then((reCaptchaToken) =>
        currentUser
          .update(
            {
              firstName: data.firstName,
              lastName: data.lastName,
              phoneNumber: data.phoneNumber,
            },
            reCaptchaToken
          )
          .then(() => {
            addSuccessNotification({
              content: 'User data updated',
            });
          })
          .catch((error) => {
            addDangerNotification({
              content: error.message,
            });
          })
      )
      .catch((err: Error) => {
        addDangerNotification({
          content: err.message,
        });
      });

    if (data.newPassword) {
      if (data.newPassword !== data.newPasswordRepeat) {
        addDangerNotification({
          content: 'Passwords are not the same',
        });
        return;
      }

      UserRepository.updatePassword({
        newPassword: data.newPassword ?? '',
        currentPassword: data.currentPassword ?? '',
      })
        .then(() => {
          addSuccessNotification({
            content: 'Password changed',
          });
        })
        .catch((error) => {
          addDangerNotification({
            content: error.message,
          });
        });
    }

    if (getValues('newPassword') || getValues('currentPassword') || getValues('newPasswordRepeat')) {
      setValue('currentPassword', '');
      setValue('newPassword', '');
      setValue('newPasswordRepeat', '');
    }
  };

  const leavePlatformMutation = useMutation({
    mutationFn: async ({ platformId }: { platformId: string }) => {
      if (!currentUser) throw new Error('No user found');
      return UserRepository.removeUserFromPlatform({
        dashboardUserId: currentUser.id,
        platformId,
      });
    },
    onSuccess: async (_, { platformId }) => {
      const wasCurrentPlatform = currentPlatform?.id === platformId;

      await refetchCurrentUser();

      if (wasCurrentPlatform) {
        const { currentUser: updatedUser } = useSessionStore.getState();
        if (!updatedUser) {
          addDangerNotification({
            content: 'Failed to refresh user data',
          });
          return;
        }

        const ownerPlatform = updatedUser.platformRoles?.find((role: PlatformRole) => role.name === 'owner');

        if (ownerPlatform) {
          const updatedUserModel = new UserModel({
            ...updatedUser,
            defaultPlatformId: ownerPlatform.platformId,
          });

          const newPlatformRole = updatedUser.platformRoles?.find(
            (role: PlatformRole) => role.platformId === ownerPlatform.platformId
          );

          if (newPlatformRole) {
            flushSync(() => {
              setCurrentUser(updatedUserModel);
              setPlatform(undefined, newPlatformRole);
            });

            loadPlatform({ user: updatedUserModel })
              .then(() =>
                executeReCAPTCHA(RecaptchaAction.UpdateUser)
                  .then((reCaptchaToken) =>
                    updatedUserModel
                      .update({ defaultPlatformId: ownerPlatform.platformId }, reCaptchaToken)
                      .then(() => {
                        log({
                          name: 'Platform changed after leaving',
                          context: updatedUserModel,
                        });
                      })
                      .catch((error) => {
                        addDangerNotification({
                          content: error.message,
                        });
                      })
                  )
                  .catch((err: Error) => {
                    addDangerNotification({
                      content: err.message,
                    });
                  })
              )
              .catch((error) => {
                addDangerNotification({
                  content: error.message || 'Failed to switch platform',
                });
              });
          }
        }
      }

      addSuccessNotification({
        content: 'Successfully left platform',
      });
    },
    onError: (error: any) => {
      addDangerNotification({
        content: error.message || 'Failed to leave platform',
      });
    },
  });

  const handleLeavePlatform = useCallback(
    (platformName: string, platformId: string, e?: React.MouseEvent) => {
      e?.stopPropagation();
      openAlert({
        headline: `Leave ${platformName}?`,
        content: `You will lose all access to this platform and its associated data. Are you sure you want to leave ${platformName}?`,
        confirmButton: {
          label: 'Leave Platform',
          variant: 'danger',
        },
        onSuccess: () => {
          leavePlatformMutation.mutate({ platformId });
        },
      });
    },
    [openAlert, leavePlatformMutation]
  );

  const platformList: PlatformRow[] = useMemo(() => {
    if (!currentUser?.platformRoles) return [];
    return currentUser.platformRoles.map((platformRole) => ({
      id: platformRole.platformId,
      platformName: platformRole.platformName || 'Unknown Platform',
      roleName: platformRole.name,
      platformId: platformRole.platformId,
      isOwner: platformRole.name === 'owner',
      isLiveEnabled: platformRole.isLiveEnabled,
    }));
  }, [currentUser?.platformRoles]);

  const platformColumns: TableColumn<PlatformRow>[] = useMemo(
    () => [
      {
        header: 'Platform Name',
        accessor: 'platformName',
        width: { min: 300, max: '1fr' },
        cell: ({ value }) => (
          <Table.Cell>
            <Text size={14} weight="medium">
              {value}
            </Text>
          </Table.Cell>
        ),
      },
      {
        header: (
          <RoleHelpButton
            icon={<Icon.CircleQuestionmark />}
            onClick={() => openHelpSidebar('Roles')}
            variant="subtle"
            size="small"
          >
            Role
          </RoleHelpButton>
        ),
        accessor: 'roleName',
        width: { min: 'min-content', max: 'min-content' },
        cell: ({ value }) => <Table.ChipCell value={getRoleLabel(value)} variant="subtle" size="large" />,
      },
      {
        header: 'Platform ID',
        accessor: 'platformId',
        width: { min: 312, max: 330 },
        cell: ({ value }) => (
          <Table.Cell>
            <CopyInput value={value} size="small" isMonospaced variant="text-only" fullWidth />
          </Table.Cell>
        ),
      },
    ],
    [openHelpSidebar]
  );

  return (
    <>
      <PageActions>
        <Button type="button" size="small" onClick={handleSubmit(handleSave, handleFormError)}>
          Save changes
        </Button>
      </PageActions>
      {currentPlatform && !isAllowedAppAccess(currentPlatform, mfaStatus, featureFlagRequireLiveVerification) && (
        <Inner pb={0}>
          <Notification color="danger" variant="light" withClose={false}>
            Additional verification is required to access this platform. Please enable two-factor authentication.
          </Notification>
        </Inner>
      )}
      <FormProvider {...formMethods}>
        <form autoComplete="off" onSubmit={handleSubmit(handleSave, handleFormError)}>
          {RenderFields<CustomDashboardUser>({
            sections: [
              {
                headline: 'Personal Information',
                fields: [
                  {
                    id: 'firstName',
                    label: 'First Name',
                    rules: { required: true },
                    children: ({ value, onChange, onBlur, ref }, { isTouched, error }) => {
                      return (
                        <Input
                          value={String(value)}
                          onChange={onChange}
                          onBlur={onBlur}
                          ref={ref}
                          placeholder="First Name"
                          hasError={isTouched && !!error}
                        />
                      );
                    },
                  },
                  {
                    id: 'lastName',
                    label: 'Last Name',
                    rules: { required: true },
                    children: ({ value, onChange, onBlur, ref }, { isTouched, error }) => {
                      return (
                        <Input
                          value={String(value)}
                          onChange={onChange}
                          onBlur={onBlur}
                          ref={ref}
                          placeholder="Last Name"
                          hasError={isTouched && !!error}
                        />
                      );
                    },
                  },
                  {
                    id: 'email',
                    label: 'Email address',
                    children: ({ value }, { isTouched, error }) => {
                      const isEmailVerified = value === currentUser.email && currentUser.emailState === 'VERIFIED';
                      const isEmailPending =
                        value === currentUser.email && currentUser.emailState === 'VERIFY_IN_PROGRESS';

                      return (
                        <FormElementAction gap={isEmailVerified ? 0 : undefined}>
                          <FormFieldStatusWrapper>
                            <StyledEmailInput
                              value={String(value)}
                              placeholder="Email Address"
                              autoComplete="off"
                              hasError={isTouched && !!error}
                              isDisabled
                            />

                            <FormFieldStatus ref={notificationContainer}>
                              <FieldStatus show={isEmailPending} color="pending">
                                <StyledTooltip
                                  content="A verification email has been sent. Please check your inbox to complete verification."
                                  placement="top"
                                >
                                  Pending
                                </StyledTooltip>
                                <PendingIcon />
                              </FieldStatus>

                              <FieldStatus show={isEmailVerified} color="success">
                                Verified <Icon.Checkmark />
                              </FieldStatus>
                              <FieldStatus show={!isEmailPending && !isEmailVerified} color="warning">
                                <StyledTooltip
                                  content="Please verify your email address. Verification is required to set up two-factor authentication."
                                  placement="top"
                                >
                                  Not verified
                                </StyledTooltip>
                                <Icon.Warning />
                              </FieldStatus>
                            </FormFieldStatus>
                          </FormFieldStatusWrapper>

                          {!isEmailVerified &&
                            (isEmailPending ? (
                              <Resend
                                type="button"
                                onClick={() => handleVerifyEmail(true)}
                                icon={<Icon.Mail />}
                                variant="secondary"
                              >
                                Resend
                              </Resend>
                            ) : (
                              <PlaneButton
                                onDone={() => handleVerifyEmail()}
                                icon={<Icon.Mail />}
                                success={
                                  <Button
                                    onClick={() => handleVerifyEmail()}
                                    icon={<Icon.Mail />}
                                    variant="secondary"
                                    size="default"
                                  >
                                    Resend
                                  </Button>
                                }
                              >
                                Verify
                              </PlaneButton>
                            ))}
                        </FormElementAction>
                      );
                    },
                  },
                ],
              },
              {
                headline: 'Passkeys',
                description:
                  'Sign in to your account with secure, one-click authentication. Passkeys are more secure than passwords and the strongest protection against attacks like phishing.',
                hide: !passkeyAuthEnabled,
                notifications: passkeyAuthEnabled && <AddPasskeyButton />,
                content: passkeyAuthEnabled && <SectionPasskeys />,
              },
              {
                headline: 'Two-factor authentication',
                fields: [
                  {
                    id: 'phoneNumber',
                    label: 'Phone Number',
                    children: ({ onChange }) => {
                      return <SectionMfaSms onPhoneNumberChange={onChange} />;
                    },
                  },
                  {
                    id: 'temporaryData.authenticatorApp',
                    label: 'Authenticator App',
                    hide: !deviceMFAEnabled,
                    children: () => {
                      return deviceMFAEnabled && <SectionMfaTotp />;
                    },
                  },
                ],
              },
              {
                headline: 'Password',
                fields: [
                  {
                    id: 'newPassword',
                    label: 'New Password',
                    children: ({ value, onChange, onBlur, ref }, { isTouched, error }) => {
                      return (
                        <PasswordInput
                          value={value === undefined ? '' : String(value)}
                          onChange={onChange}
                          onBlur={onBlur}
                          ref={ref}
                          placeholder="New Password"
                          hasError={isTouched && !!error}
                        />
                      );
                    },
                  },
                  {
                    id: 'newPasswordRepeat',
                    label: 'Repeat Password',
                    children: ({ value, onChange, onBlur, ref }, { isTouched, error }) => {
                      return (
                        <PasswordInput
                          value={value === undefined ? '' : String(value)}
                          onChange={onChange}
                          onBlur={onBlur}
                          ref={ref}
                          placeholder="Repeat Password"
                          hasError={isTouched && !!error}
                        />
                      );
                    },
                  },
                  {
                    id: 'currentPassword',
                    label: 'Current Password',
                    children: ({ value, onChange, onBlur, ref }, { isTouched, error }) => {
                      return (
                        <PasswordInput
                          value={value === undefined ? '' : String(value)}
                          onChange={onChange}
                          onBlur={onBlur}
                          ref={ref}
                          placeholder="Current Password"
                          hasError={isTouched && !!error}
                        />
                      );
                    },
                  },
                ],
              },
            ],
          })}

          <Inner px="var(--page-margin)">
            <Action>
              <Button type="submit" size="small">
                Save changes
              </Button>
            </Action>
          </Inner>
        </form>
      </FormProvider>
      {platformList.length > 0 && (
        <>
          <SectionHeader text="Platforms" border />
          <Inner pt={0} px="var(--page-margin)">
            <Table
              columns={platformColumns}
              data={platformList}
              variant={TableVariant.List}
              getRowId={(row) => row.id}
              rowAction={(row) =>
                !row.isOwner ? (
                  <LeaveButton
                    variant="secondary-danger"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleLeavePlatform(row.platformName, row.platformId, e);
                    }}
                  >
                    Leave
                  </LeaveButton>
                ) : null
              }
            />
          </Inner>
        </>
      )}
    </>
  );
};
